/**
 * Asset Bulk Upload Step — the XLSX-upload sub-flow of the asset
 * wizard, reached from `asset-method`'s "Upload spreadsheet" card.
 *
 * Renders template-download (+ staff-only seed-data generator) plus a
 * dropzone. On parse success, fans out N `StagedAsset`s with a shared
 * `bulkGroupId` and routes straight to `asset-activities` (skips
 * `asset-details` since every asset's metadata is already in the
 * spreadsheet).
 *
 * No in-card Back button — wizard chrome owns Back. Chrome Back from
 * here returns to `asset-method` so the user can switch path without
 * re-picking type.
 */

import { useState } from 'react';
import { Download, AlertTriangle, FlaskConical } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { ImportFileDropzone, MINE_TYPES } from '@features/files/ImportFileDropZone';
import { downloadBlob } from '@utils/downloadBlob';
import {
  AssetType,
  type CreateAssetRequest,
  type ActivityType,
  type ActivityUnit,
  type FuelType,
  type VehicleType,
  type RefrigerantGasType,
  type FugitiveGasType,
  type ProcessType,
} from '../../types/emissions';
import {
  isBulkImportableAssetType,
  type BulkImportableAssetType,
} from '@g17eco/emissions-config';
import {
  useLazyGetAssetImportTemplateQuery,
  useLazyGetAssetSeedTemplateQuery,
} from '../../api/emissions';
import { parseAssetImport, type AssetParseResult } from '../../utils/assetImportParser';
import { ASSET_TYPE_BRANDING } from '../../config/assetTypeBranding';
import {
  type StagedAsset,
  createStagedRow,
  type AssetActivityEntry,
} from './types';
import { SeedTemplateModal, type SeedTemplateParams } from './SeedTemplateModal';
import { useAppSelector } from '../../../../reducers';
import { isStaff as isStaffSelector } from '../../../../selectors/user';

/**
 * Convert `parseAssetImport`'s `Partial<CreateAssetRequest>` + parsed
 * activity list into `StagedAsset[]` with a shared `bulkGroupId`. Only
 * `isValid` rows ride through; the parser's invalid rows surface in
 * the parse-result error report (we double-guard here).
 */
const buildStagedAssetsFromParse = (
  result: AssetParseResult,
  assetType: AssetType,
  bulkGroupId: string,
): StagedAsset[] =>
  result.assets
    .filter((parsed) => parsed.isValid)
    .map((parsed) => {
      const asset: CreateAssetRequest = {
        ...parsed.asset,
        // Default name + assetType last so they win over partial parser
        // values. The picker type is the source of truth — the user
        // chose a single type for the whole template.
        name: parsed.asset.name ?? '',
        assetType,
      };
      const activities = parsed.activities.map((a) => {
        // The parser produces a structurally similar shape with looser
        // typing (string fields instead of enum unions). Coerce here
        // so downstream factor-search + submit stay strongly typed.
        const entry: AssetActivityEntry = {
          scope: a.scope,
          activityType: a.activityType as ActivityType,
          quantity: String(a.quantity),
          unit: (a.unit as ActivityUnit) ?? '',
          fuelType: (a.fuelType as FuelType | undefined) ?? '',
          vehicleType: (a.vehicleType as VehicleType | undefined) ?? '',
          refrigerantType: (a.refrigerantType as RefrigerantGasType | FugitiveGasType | undefined) ?? '',
          processType: (a.processType as ProcessType | undefined) ?? '',
          region: a.region,
          regionFallback: a.regionFallback,
          yearFallback: a.yearFallback,
          periodStart: a.periodStart,
          periodEnd: a.periodEnd,
          name: a.name ?? '',
          dataSource: '',
          notes: a.notes ?? '',
        };
        return createStagedRow(entry, bulkGroupId);
      });
      return {
        id: crypto.randomUUID(),
        bulkGroupId,
        asset,
        activities,
      };
    });

interface AssetBulkUploadStepProps {
  initiativeId: string;
  /** The asset type chosen on `asset-select`. */
  assetType: AssetType;
  /**
   * Parsed XLSX produced these stagedAssets. The parent stamps them
   * onto `formData.stagedAssets` and routes to `asset-activities`. The
   * source `File` is held in `stagedBulkGroups` so submit attaches it
   * as evidence on the per-group `createBulkAssetImport` call.
   */
  onBulkParsed: (assets: StagedAsset[], file: File, bulkGroupId: string) => void;
}

export const AssetBulkUploadStep = ({
  initiativeId,
  assetType,
  onBulkParsed,
}: AssetBulkUploadStepProps) => {
  const [importFile, setImportFile] = useState<File | undefined>();
  const [parseError, setParseError] = useState<string | null>(null);
  // Per-row validation errors from the parser. Surfaced under the
  // dropzone so the user sees *what's wrong with which row* instead of
  // a single opaque "All rows in the file have validation errors" line.
  const [invalidParseResult, setInvalidParseResult] = useState<AssetParseResult | null>(null);
  const [seedModalOpen, setSeedModalOpen] = useState(false);

  const [fetchTemplate, { isFetching: isDownloadingTemplate }] = useLazyGetAssetImportTemplateQuery();
  const [fetchSeedTemplate, { isFetching: isDownloadingSeedTemplate }] = useLazyGetAssetSeedTemplateQuery();
  const isStaff = useAppSelector(isStaffSelector);

  const brand = ASSET_TYPE_BRANDING[assetType] ?? ASSET_TYPE_BRANDING[AssetType.Other];
  const canBulk = isBulkImportableAssetType(assetType);

  const handleDownloadTemplate = async () => {
    if (!canBulk) {
      return;
    }
    const result = await fetchTemplate({
      initiativeId,
      assetType: assetType as BulkImportableAssetType,
    });
    if (result.data) {
      downloadBlob(result.data, `emissions-${assetType}-import-template.xlsx`);
      return;
    }
    setParseError('Template download failed. Please try again.');
  };

  const handleDownloadSeedTemplate = async ({ rowCount, startYear, endYear }: SeedTemplateParams) => {
    if (!canBulk) {
      return;
    }
    const result = await fetchSeedTemplate({
      initiativeId,
      assetType: assetType as BulkImportableAssetType,
      rowCount,
      startYear,
      endYear,
    });
    if (result.data) {
      downloadBlob(result.data, `emissions-${assetType}-seed-template.xlsx`);
    }
  };

  const handleFilesAdded = async (files: File[]) => {
    const file = files[0];
    if (!file || !canBulk) {
      return;
    }
    setImportFile(file);
    setParseError(null);
    setInvalidParseResult(null);

    try {
      const result = await parseAssetImport(file, assetType as BulkImportableAssetType);
      if (result.assets.length === 0) {
        setParseError('No assets found in the file. Make sure you filled in at least one row with asset data.');
        return;
      }
      const bulkGroupId = crypto.randomUUID();
      const stagedAssets = buildStagedAssetsFromParse(result, assetType, bulkGroupId);
      if (stagedAssets.length === 0) {
        // Stash the parse result so the per-row error report below
        // can show *what's wrong* — a single line saying "all rows
        // failed" gave the user nothing actionable.
        setParseError(`All ${result.assets.length} row${result.assets.length === 1 ? '' : 's'} have validation errors. Fix the issues below and re-upload.`);
        setInvalidParseResult(result);
        return;
      }
      onBulkParsed(stagedAssets, file, bulkGroupId);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Failed to parse file');
    }
  };

  return (
    <Card className='tw:mb-6'>
      <CardContent className='tw:p-6'>
        <div className='tw:flex tw:items-center tw:gap-3 tw:mb-4'>
          <div className={`tw:rounded-full tw:flex tw:items-center tw:justify-center tw:size-10 ${brand.iconBg}`}>
            <brand.Icon className={`tw:size-5 ${brand.iconColor}`} />
          </div>
          <div>
            <h5 className='tw:font-semibold tw:mb-0'>{brand.label}</h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>{brand.description}</p>
          </div>
        </div>

        <div className='tw:rounded-lg tw:border tw:border-border tw:p-4 tw:mb-4'>
          <div className='tw:flex tw:justify-between tw:items-center tw:gap-4'>
            <div>
              <h6 className='tw:font-semibold tw:mb-1'>Step 1 — Download Template</h6>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                Download the {brand.label} XLSX template and fill it in with your asset data.
              </p>
            </div>
            <div className='tw:flex tw:gap-2 tw:shrink-0'>
              <Button variant='outline' onClick={handleDownloadTemplate} disabled={isDownloadingTemplate}>
                <Download className='tw:size-4' />
                {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
              </Button>
              {/*
                * Staff-only seed-template generator. Mirrors the
                * Activity bulk flow's button so internal users can
                * grab a realistic prefilled template for testing.
                */}
              {isStaff && (
                <Button variant='outline' onClick={() => setSeedModalOpen(true)} disabled={isDownloadingSeedTemplate}>
                  <FlaskConical className='tw:size-4' />
                  {isDownloadingSeedTemplate ? 'Generating...' : 'Seed Template'}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className='tw:rounded-lg tw:border tw:border-border tw:p-4'>
          <div className='tw:mb-4'>
            <h6 className='tw:font-semibold tw:mb-1'>Step 2 — Upload Your File</h6>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
              Each row in the template represents one {brand.label.toLowerCase()} with all its emission activities.
            </p>
          </div>

          <ImportFileDropzone
            importFile={importFile}
            onFilesAdded={handleFilesAdded}
            onFileRemove={() => { setImportFile(undefined); setParseError(null); setInvalidParseResult(null); }}
            accept={{ ...MINE_TYPES.EXCEL }}
            dropText='Drag and drop your Excel file here'
            hint='Supported format: XLSX'
          />

          {parseError && (
            <div className='tw:flex tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-red-800 tw:text-sm tw:mt-3'>
              <AlertTriangle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
              {parseError}
            </div>
          )}

          {/*
            * Per-row error report — surfaces what each row got wrong
            * (e.g. "Row 3 — Vehicle Type: Invalid vehicle type 'sedan'").
            * Only rendered when the parse failed validation; valid
            * uploads route straight through to `asset-activities`.
            */}
          {invalidParseResult && (
            <div className='tw:mt-3 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50/50'>
              <div className='tw:px-4 tw:py-2 tw:border-b tw:border-red-200 tw:text-sm tw:font-semibold tw:text-red-900'>
                Validation errors
              </div>
              <div className='tw:max-h-80 tw:overflow-y-auto tw:divide-y tw:divide-red-100'>
                {invalidParseResult.assets
                  .filter((parsed) => parsed.errors.length > 0)
                  .map((parsed) => (
                    <div key={parsed.index} className='tw:px-4 tw:py-2 tw:text-sm'>
                      <div className='tw:font-medium tw:text-red-900 tw:mb-1'>
                        Row {parsed.index + 1}
                        {parsed.asset.name ? ` — ${parsed.asset.name}` : ''}
                      </div>
                      <ul className='tw:list-disc tw:list-inside tw:text-red-800 tw:space-y-0.5'>
                        {parsed.errors.map((err, i) => (
                          <li key={i}>
                            <span className='tw:font-medium'>{err.field}:</span> {err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {isStaff && (
          <SeedTemplateModal
            isOpen={seedModalOpen}
            toggle={() => setSeedModalOpen(false)}
            onDownload={(params) => {
              handleDownloadSeedTemplate(params);
              setSeedModalOpen(false);
            }}
            isDownloading={isDownloadingSeedTemplate}
          />
        )}
      </CardContent>
    </Card>
  );
};
